import React, { useEffect, useState } from 'react';
import { t } from '@lingui/core/macro';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { Block } from '../classes/Block';
import './CopyFormattingDialog.css';

export type FormattingGroup = 'layout' | 'label' | 'value' | 'all';

interface CopyFormattingDialogProps {
  group: FormattingGroup | null;
  sourceBlockName: string;
  sourceBlockId?: string;
  targetBlocks: Block[];
  blockNumbers: Map<string, number>;
  sideBadgeColors?: ReadonlyMap<string, string>;
  sideFilter?: { side: 'left' | 'right'; enabled: boolean; onToggle: () => void };
  selectedBlockIds: Set<string>;
  onToggleBlock: (blockId: string) => void;
  onToggleAll: () => void;
  onClose: () => void;
  onApply: () => void;
}

const getGroupLabel = (group: FormattingGroup | null): string => {
  if (group === 'layout') return t`Block Layout`;
  if (group === 'label') return t`Label Formatting`;
  if (group === 'value') return t`Value Formatting`;
  if (group === 'all') return t`Block Layout, Label, and Value Formatting`;
  return '';
};

const CopyFormattingDialog: React.FC<CopyFormattingDialogProps> = ({
  group,
  sourceBlockName,
  sourceBlockId = '',
  targetBlocks,
  blockNumbers,
  sideBadgeColors,
  sideFilter,
  selectedBlockIds,
  onToggleBlock,
  onToggleAll,
  onClose,
  onApply,
}) => {
  const [search, setSearch] = useState('');
  useEffect(() => { setSearch(''); }, [group, sourceBlockName]);
  const query = search.trim().toLocaleLowerCase();
  const visibleBlocks = targetBlocks.filter((block: Block) =>
    block.name.toLocaleLowerCase().includes(query) ||
    (blockNumbers.get(block.id)?.toString() ?? '').includes(query)
  );
  const selectableBlocks: Block[] = targetBlocks.filter((block: Block) => !block.isLocked);
  const selectedSelectableBlockCount: number = selectableBlocks.filter(
    (block: Block) => selectedBlockIds.has(block.id)
  ).length;
  const allSelected: boolean = selectedSelectableBlockCount === selectableBlocks.length && selectableBlocks.length > 0;
  const someSelected: boolean = selectedSelectableBlockCount > 0 && selectedSelectableBlockCount < selectableBlocks.length;

  return (
    <Dialog className="copy-formatting-modal" open={group !== null} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="copy-formatting-title">
      <DialogTitle>
        <span id="copy-formatting-title">{t`Copy to Other Blocks`}</span>
        <IconButton onClick={onClose} aria-label={t`Close`} size="small"><CloseRounded fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent className="copy-formatting-dialog">
        <dl className="copy-formatting-context">
          <div><dt>{t`Source block`}</dt><dd><span className="copy-formatting-block-name">{blockNumbers.has(sourceBlockId) && <span className="copy-formatting-block-number" aria-hidden="true">{blockNumbers.get(sourceBlockId)}</span>}{sourceBlockName}{sideFilter && <span className="copy-formatting-side-badge" style={{ backgroundColor: sideBadgeColors?.get(sourceBlockId), color: sideBadgeColors?.has(sourceBlockId) ? '#fff' : undefined }} aria-hidden="true">{sideFilter.side === 'left' ? 'L' : 'R'}</span>}</span></dd></div>
          <div><dt>{t`Formatting`}</dt><dd>{getGroupLabel(group)}</dd></div>
        </dl>
        <div className="copy-formatting-search">
          <SearchRounded fontSize="small" aria-hidden="true" />
          <input type="search" aria-label={t`Search blocks`} placeholder={t`Search blocks`} value={search} onChange={(event) => setSearch(event.target.value)} />
          {search && <IconButton size="small" onClick={() => setSearch('')} aria-label={t`Clear search`}><CloseRounded fontSize="small" /></IconButton>}
        </div>
        {sideFilter && (
          <FormControlLabel
            className="copy-formatting-side-filter"
            control={<Switch size="small" checked={sideFilter.enabled} onChange={sideFilter.onToggle} />}
            label={sideFilter.side === 'left' ? t`Left side only` : t`Right side only`}
          />
        )}
        <FormGroup>
          <div className="copy-formatting-selection">
            <FormControlLabel
              control={
                <Checkbox
                  disableRipple
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onToggleAll}
                  color="primary"
                  disabled={selectableBlocks.length === 0}
                />
              }
              label={<strong>{allSelected ? t`Unselect All` : t`Select All`}</strong>}
            />
            <span role="status" className="copy-formatting-count">{t`${selectedSelectableBlockCount} selected`}</span>
          </div>
          <div className="copy-formatting-dialog-list" role="group" aria-label={t`Target blocks`}>
            {visibleBlocks.map((block: Block) => (
              <FormControlLabel
                key={block.id}
                className={`copy-formatting-row${selectedBlockIds.has(block.id) ? ' is-selected' : ''}`}
                disabled={block.isLocked}
                control={
                  <Checkbox
                    disableRipple
                    checked={selectedBlockIds.has(block.id)}
                    onChange={() => {
                      if (!block.isLocked) {
                        onToggleBlock(block.id);
                      }
                    }}
                    color="primary"
                    disabled={block.isLocked}
                  />
                }
                label={<><span className="copy-formatting-block-name"><span className="copy-formatting-block-number" aria-hidden="true">{blockNumbers.get(block.id)}</span>{block.name}{(block.sbsBlocks === 'left' || block.sbsBlocks === 'right') && <span className="copy-formatting-side-badge" style={{ backgroundColor: sideBadgeColors?.get(block.id), color: sideBadgeColors?.has(block.id) ? '#fff' : undefined }} aria-hidden="true">{block.sbsBlocks === 'left' ? 'L' : 'R'}</span>}</span>{block.isLocked && <LockOutlined fontSize="small" aria-hidden="true" />}</>}
              />
            ))}
            {visibleBlocks.length === 0 && <p className="copy-formatting-empty" role="status">{targetBlocks.length === 0 ? (sideFilter?.enabled ? t`No blocks on this side. Turn off the filter to see other blocks.` : t`No compatible blocks available.`) : t`No blocks match your search.`}</p>}
          </div>
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{t`Cancel`}</Button>
        <Button
          onClick={onApply}
          variant="contained"
          disableElevation
          disabled={selectedBlockIds.size === 0}
        >
          {t`Apply`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyFormattingDialog;
